// Clicks on dashboard anchors must not navigate this document. The
// iframe's URL is the sandbox origin, so a root-relative href such as
// `/plant` would replace the renderer instead of moving Axilon. The
// parent (`SandboxFrame`) receives `sandbox-navigate` and routes the
// top-level page. Hash-only hrefs stay here so in-dashboard anchors
// still scroll.

import type { SandboxNavigateMessage } from './sandbox-protocol'

const SAME_FRAME_TARGETS = new Set(['', '_self', '_parent', '_top'])

export interface AnchorActivation {
  href: string | null
  target: string | null
  download: boolean
  button: number
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
  defaultPrevented: boolean
}

export type AnchorPlan =
  | { type: 'ignore' }
  | { type: 'cancel' }
  | { type: 'navigate'; href: string; newTab: boolean }

/**
 * Decide what a click on an anchor should do. `ignore` leaves the
 * browser default in place (in-dashboard hashes, downloads, clicks the
 * dashboard code already handled). `cancel` suppresses iframe
 * navigation without telling the parent (empty hrefs reload this
 * document). `navigate` is posted to the parent.
 */
export function planAnchorActivation(activation: AnchorActivation): AnchorPlan {
  if (activation.defaultPrevented) return { type: 'ignore' }
  if (activation.download) return { type: 'ignore' }
  if (activation.button !== 0 && activation.button !== 1) return { type: 'ignore' }

  const href = activation.href
  if (href === null) return { type: 'ignore' }
  const trimmed = href.trim()
  if (trimmed.startsWith('#')) return { type: 'ignore' }
  if (trimmed === '') return { type: 'cancel' }

  const target = (activation.target ?? '').trim().toLowerCase()
  const newTab =
    activation.button === 1 ||
    activation.metaKey ||
    activation.ctrlKey ||
    activation.shiftKey ||
    activation.altKey ||
    (target !== '' && !SAME_FRAME_TARGETS.has(target))

  return { type: 'navigate', href: trimmed, newTab }
}

function elementFromEventTarget(target: EventTarget | null): Element | null {
  if (target instanceof Element) return target
  if (target instanceof Text) return target.parentElement
  return null
}

export function readAnchorActivation(event: MouseEvent): AnchorActivation | null {
  const element = elementFromEventTarget(event.target)
  const anchor = element?.closest('a, area')
  if (anchor === null || anchor === undefined) return null
  return {
    href: anchor.getAttribute('href'),
    target: anchor.getAttribute('target'),
    download: anchor.hasAttribute('download'),
    button: event.button,
    metaKey: event.metaKey,
    ctrlKey: event.ctrlKey,
    shiftKey: event.shiftKey,
    altKey: event.altKey,
    defaultPrevented: event.defaultPrevented
  }
}

export function installDashboardLinkBridge(
  target: Document,
  post: (message: SandboxNavigateMessage) => void
): () => void {
  const onActivate = (event: Event): void => {
    // Dashboard code is untrusted. Only a physical browser activation may
    // escape the sandbox through the parent navigation bridge.
    if (!(event instanceof MouseEvent) || !event.isTrusted) return
    const activation = readAnchorActivation(event)
    if (activation === null) return
    const plan = planAnchorActivation(activation)
    if (plan.type === 'ignore') return
    event.preventDefault()
    if (plan.type !== 'navigate') return
    post({ type: 'sandbox-navigate', href: plan.href, newTab: plan.newTab })
  }
  target.addEventListener('click', onActivate)
  target.addEventListener('auxclick', onActivate)
  return () => {
    target.removeEventListener('click', onActivate)
    target.removeEventListener('auxclick', onActivate)
  }
}
